import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type TemperatureUnit = "fahrenheit" | "celsius";

interface TemperatureUnitContextType {
  unit: TemperatureUnit;
  setUnit: (unit: TemperatureUnit) => void;
  toDisplay: (fahrenheit: number) => number;
  toFahrenheit: (value: number) => number;
  formatTemp: (fahrenheit: number) => string;
  symbol: string;
}

const TemperatureUnitContext = createContext<TemperatureUnitContextType | undefined>(undefined);

export const TemperatureUnitProvider = ({ children }: { children: ReactNode }) => {
  const [unit, setUnit] = useState<TemperatureUnit>(() => {
    const saved = localStorage.getItem("temperatureUnit");
    return (saved as TemperatureUnit) || "fahrenheit";
  });

  useEffect(() => {
    localStorage.setItem("temperatureUnit", unit);
  }, [unit]);

  const toDisplay = (fahrenheit: number): number => {
    if (unit === "celsius") {
      return Number(((fahrenheit - 32) * (5 / 9)).toFixed(1));
    }
    return Number(fahrenheit.toFixed(1));
  };

  const toFahrenheit = (value: number): number => {
    if (unit === "celsius") {
      return Number((value * (9 / 5) + 32).toFixed(1));
    }
    return value;
  };

  const formatTemp = (fahrenheit: number): string => {
    return `${toDisplay(fahrenheit)}°${unit === "celsius" ? "C" : "F"}`;
  };

  const symbol = unit === "celsius" ? "°C" : "°F";

  return (
    <TemperatureUnitContext.Provider value={{ unit, setUnit, toDisplay, toFahrenheit, formatTemp, symbol }}>
      {children}
    </TemperatureUnitContext.Provider>
  );
};

export const useTemperatureUnit = () => {
  const context = useContext(TemperatureUnitContext);
  if (!context) {
    throw new Error("useTemperatureUnit must be used within a TemperatureUnitProvider");
  }
  return context;
};
